import { createContext, use, useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { X } from 'lucide-react'
import type { ReactNode } from 'react'

import type {
    CatalogNames,
    LinkForm,
} from '@/components/retail/home-form-model'
import {
    LINK_CATEGORIES_MAX,
    LINK_TYPES,
    PRODUCT_TYPE_KEYS,
    linkTypeLabel,
    productTypeLabel,
} from '@/components/retail/home-form-model'
import { TextField } from '@/components/retail/form-bits'
import { Badge } from '@/components/ui/badge'
import { Combobox } from '@/components/ui/combobox'
import { FieldError } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    useShopCategories,
    useShopGroups,
} from '@/hooks/use-shop-catalog-query'
import { m } from '@/lib/paraglide/messages'

// Danh mục bán + nhóm SP để chọn đích link theo tên (lấy slug). Danh sách cần
// quyền shop_catalog.read: người chỉ có quyền nội dung thì gõ slug tay.

interface CatalogOption {
    slug: string
    name: string
}

interface HomeCatalog {
    available: boolean
    categories: CatalogOption[]
    groups: CatalogOption[]
}

const EMPTY_CATALOG: HomeCatalog = {
    available: false,
    categories: [],
    groups: [],
}

const HomeCatalogContext = createContext<HomeCatalog>(EMPTY_CATALOG)

/** Nạp danh mục + nhóm SP cho các ô link; chỉ render khi có shop_catalog.read. */
export function HomeCatalogProvider({ children }: { children: ReactNode }) {
    const categories = useShopCategories().data?.data
    const groups = useShopGroups({}).data?.data
    const value = useMemo<HomeCatalog>(
        () => ({
            available: true,
            categories: (categories ?? []).map((c) => ({
                slug: c.slug,
                name: c.name,
            })),
            groups: (groups ?? []).map((g) => ({ slug: g.slug, name: g.name })),
        }),
        [categories, groups],
    )
    return <HomeCatalogContext value={value}>{children}</HomeCatalogContext>
}

/** slug → tên, để tóm tắt đích link ("Danh mục › Chè Tôm Nõn"). */
export function useCatalogNames(): CatalogNames {
    const catalog = use(HomeCatalogContext)
    return useMemo(
        () => ({
            categories: new Map(
                catalog.categories.map((c) => [c.slug, c.name]),
            ),
            groups: new Map(catalog.groups.map((g) => [g.slug, g.name])),
        }),
        [catalog],
    )
}

const NO_LINK = 'none'

/**
 * Ô đích link dùng chung các khối trang chủ: chọn loại (Danh mục · Nhóm SP ·
 * Danh sách SP theo danh mục · Tìm kiếm · Bài viết · Trang · URL ngoài · Gọi
 * điện · Zalo) rồi ô nhập theo loại. `name` là đường dẫn object link trong form
 * (vd items.0.data.link) — trùng params.field server trả nên lỗi hiện đúng ô.
 */
export function HomeLinkField({
    name,
    label,
    optional,
}: {
    name: string
    label: string
    /** Cho phép "Không có link" (link tuỳ chọn). */
    optional?: boolean
}) {
    const { control } = useFormContext()
    const type = (useWatch({ control, name: `${name}.type` }) as string) || ''
    const typeId = `${name}.type`
    return (
        <fieldset className="space-y-3 rounded-md border p-3">
            <legend className="px-1 text-sm font-medium">{label}</legend>
            <div className="space-y-1.5">
                <Label htmlFor={typeId} required={!optional}>
                    {m.shop_home_link_type()}
                </Label>
                <Controller
                    control={control}
                    name={typeId}
                    render={({ field }) => (
                        <Select
                            value={(field.value as string) || NO_LINK}
                            onValueChange={(v) =>
                                field.onChange(v === NO_LINK ? '' : v)
                            }
                        >
                            <SelectTrigger id={typeId} className="w-full">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value={NO_LINK}>
                                    {optional
                                        ? m.shop_home_link_none()
                                        : m.shop_home_link_pick()}
                                </SelectItem>
                                {LINK_TYPES.map((t) => (
                                    <SelectItem key={t} value={t}>
                                        {linkTypeLabel(t)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                />
                <FieldError name={typeId} />
            </div>
            <LinkTarget name={name} type={type} />
        </fieldset>
    )
}

function LinkTarget({ name, type }: { name: string; type: string }) {
    switch (type) {
        case 'category':
        case 'group':
            return <CatalogSlugField name={name} type={type} />
        case 'products':
            return (
                <>
                    <CategoriesField name={`${name}.categories`} />
                    <ProductTypesField name={`${name}.types`} />
                </>
            )
        case 'search':
            return (
                <TextField
                    name={`${name}.q`}
                    label={`${m.shop_home_link_q()} *`}
                    hint={m.shop_home_link_q_hint()}
                />
            )
        case 'post':
        case 'page':
            return (
                <TextField
                    name={`${name}.slug`}
                    label={`${m.shop_home_link_slug()} *`}
                    hint={m.shop_home_link_slug_hint()}
                />
            )
        case 'url':
            return (
                <TextField
                    name={`${name}.url`}
                    label={`${m.shop_home_link_url_label()} *`}
                    hint={m.shop_home_link_url_hint()}
                />
            )
        case 'tel':
        case 'zalo':
            return (
                <TextField
                    name={`${name}.phone`}
                    label={`${m.shop_home_link_phone()} *`}
                />
            )
        default:
            return null
    }
}

/** Chọn danh mục / nhóm theo tên (giữ slug); không có danh sách thì gõ slug. */
function CatalogSlugField({
    name,
    type,
}: {
    name: string
    type: 'category' | 'group'
}) {
    const { control } = useFormContext()
    const catalog = use(HomeCatalogContext)
    const slugName = `${name}.slug`
    const label =
        type === 'category'
            ? m.shop_home_link_pick_category()
            : m.shop_home_link_pick_group()
    if (!catalog.available)
        return (
            <TextField
                name={slugName}
                label={`${label} *`}
                hint={m.shop_home_link_slug_manual_hint()}
            />
        )
    const list = type === 'category' ? catalog.categories : catalog.groups
    return (
        <div className="space-y-1.5">
            <Label htmlFor={slugName} required>
                {label}
            </Label>
            <Controller
                control={control}
                name={slugName}
                render={({ field }) => {
                    const value = (field.value as string) || ''
                    const options = list.map((o) => ({
                        value: o.slug,
                        label: o.name,
                    }))
                    // Slug đã lưu nhưng không còn trong danh sách vẫn hiện.
                    if (value && !options.some((o) => o.value === value))
                        options.push({ value, label: value })
                    return (
                        <Combobox
                            id={slugName}
                            options={options}
                            value={value}
                            onChange={field.onChange}
                            placeholder={m.shop_home_link_pick()}
                            searchPlaceholder={m.shop_chip_search()}
                            emptyText={m.shop_chip_empty()}
                        />
                    )
                }}
            />
            <FieldError name={slugName} />
        </div>
    )
}

/** Nhiều danh mục bán (1–10) cho đích "Danh sách SP theo danh mục". */
function CategoriesField({ name }: { name: string }) {
    const { control } = useFormContext()
    const catalog = use(HomeCatalogContext)
    const names = useCatalogNames().categories
    return (
        <div className="space-y-1.5">
            <Label htmlFor={name}>{m.shop_home_link_categories()}</Label>
            <Controller
                control={control}
                name={name}
                render={({ field }) => {
                    const chosen = (field.value as LinkForm['categories']) ?? []
                    const set = (next: string[]) => field.onChange(next)
                    const options = catalog.categories
                        .filter((c) => !chosen.includes(c.slug))
                        .map((c) => ({ value: c.slug, label: c.name }))
                    return (
                        <div className="space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                                {chosen.map((slug, i) => (
                                    <div key={slug} className="space-y-1">
                                        <Badge
                                            variant="secondary"
                                            className="gap-1"
                                        >
                                            {names.get(slug) ?? slug}
                                            <button
                                                type="button"
                                                className="rounded-sm focus-visible:outline focus-visible:outline-2"
                                                aria-label={m.shop_chip_remove_aria(
                                                    {
                                                        name:
                                                            names.get(slug) ??
                                                            slug,
                                                    },
                                                )}
                                                onClick={() =>
                                                    set(
                                                        chosen.filter(
                                                            (_, j) => j !== i,
                                                        ),
                                                    )
                                                }
                                            >
                                                <X className="size-3" />
                                            </button>
                                        </Badge>
                                        <FieldError name={`${name}.${i}`} />
                                    </div>
                                ))}
                            </div>
                            {chosen.length < LINK_CATEGORIES_MAX &&
                                (catalog.available ? (
                                    <div className="w-full sm:w-64">
                                        <Combobox
                                            id={name}
                                            options={options}
                                            value=""
                                            placeholder={m.shop_chip_add()}
                                            searchPlaceholder={m.shop_chip_search()}
                                            emptyText={m.shop_chip_empty()}
                                            onChange={(v) =>
                                                v && set([...chosen, v])
                                            }
                                        />
                                    </div>
                                ) : (
                                    <Input
                                        id={name}
                                        placeholder={m.shop_home_link_categories_manual()}
                                        onKeyDown={(e) => {
                                            const v =
                                                e.currentTarget.value.trim()
                                            if (e.key !== 'Enter' || !v) return
                                            e.preventDefault()
                                            if (!chosen.includes(v))
                                                set([...chosen, v])
                                            e.currentTarget.value = ''
                                        }}
                                    />
                                ))}
                        </div>
                    )
                }}
            />
            <p className="text-xs text-muted-foreground">
                {m.shop_home_link_categories_hint({
                    max: String(LINK_CATEGORIES_MAX),
                })}
            </p>
            <FieldError name={name} />
        </div>
    )
}

/** Loại sản phẩm (khoá ?loai= của web) cho đích "Danh sách sản phẩm". */
function ProductTypesField({ name }: { name: string }) {
    const { control } = useFormContext()
    return (
        <fieldset className="space-y-1.5">
            <legend className="text-sm font-medium">
                {m.shop_home_link_types()}
            </legend>
            <Controller
                control={control}
                name={name}
                render={({ field }) => {
                    const chosen = (field.value as LinkForm['types']) ?? []
                    return (
                        <div className="flex flex-wrap gap-x-4 gap-y-2">
                            {PRODUCT_TYPE_KEYS.map((key) => (
                                <label
                                    key={key}
                                    className="flex min-h-11 cursor-pointer items-center gap-2 text-sm md:min-h-0"
                                >
                                    <input
                                        type="checkbox"
                                        className="size-4 accent-primary"
                                        checked={chosen.includes(key)}
                                        onChange={(e) =>
                                            field.onChange(
                                                e.target.checked
                                                    ? PRODUCT_TYPE_KEYS.filter(
                                                          (k) =>
                                                              k === key ||
                                                              chosen.includes(
                                                                  k,
                                                              ),
                                                      )
                                                    : chosen.filter(
                                                          (k) => k !== key,
                                                      ),
                                            )
                                        }
                                    />
                                    {productTypeLabel(key)}
                                </label>
                            ))}
                        </div>
                    )
                }}
            />
            <p className="text-xs text-muted-foreground">
                {m.shop_home_link_types_hint()}
            </p>
            <FieldError name={name} />
        </fieldset>
    )
}
